/**
 * Iframe Child Bridge — handles SCIO 3.0 running as a child iframe inside SCIO 1.0.
 *
 * When company.scioVersion === 3, SCIO 1.0 embeds SCIO 3.0 in a full-page iframe.
 * This module provides:
 *  - Detection of embedded mode
 *  - Promise-based postMessage communication with parent
 *  - Auth handshake (request custom token → signInWithCustomToken)
 *  - Event listener for parent commands (navigation, language, team, logout)
 *
 * See: claude-dev/2026-02-10-ted-scio-embed/00-spec.md §3–4
 */

import { SCIO1_STATE_MAP } from '@/components/iframe/scio1-route-map';

// --- Detection ---

/**
 * Static check: true when running inside ANY iframe (IDE preview, browser extension, scio1 parent, etc.).
 * Used to decide whether to ATTEMPT the parent handshake. NOT used for UI decisions.
 * For UI decisions (hide header), use `isEmbedded` from auth-store — set after confirmed handshake.
 */
export const isInIframeContext = (() => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access throws — we're in an iframe
    return true;
  }
})();

// --- Types ---

interface ParentMessage {
  event: string;
  data?: unknown;
  requestId?: string;
}

interface CustomTokenResponse {
  token: string;
  uid: string;
  teamId?: string;
}

interface FreshTokenResponse {
  token: string;
  expires: string;
  uid: string;
  isFBToken?: boolean;
}

export interface ParentEventCallbacks {
  onRouterRedirect?: (url: string, params?: Record<string, unknown>) => void;
  onChangeTeam?: (teamId: string) => void;
  onSyncLanguage?: (language: string) => void;
  onLogout?: () => void;
  onReload?: () => void;
}

// --- Promise tracking ---

const pendingRequests = new Map<
  string,
  { resolve: (data: unknown) => void; reject: (err: Error) => void }
>();

let requestCounter = 0;

function generateRequestId(): string {
  return `scio3_${Date.now()}_${++requestCounter}`;
}

// --- Core messaging ---

/** Send a JSON-stringified message to the parent window. No-op if not in iframe. */
export function postToParent(msg: ParentMessage): void {
  if (!isInIframeContext) return;
  window.parent.postMessage(JSON.stringify(msg), '*');
}

/** Send a message and await a `responsePromise` reply keyed by requestId. */
export function sendToParentWithPromise<T = unknown>(
  event: string,
  data?: unknown,
  timeoutMs = 15000,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const requestId = generateRequestId();

    const timeout = setTimeout(() => {
      pendingRequests.delete(requestId);
      reject(new Error(`Parent response timeout for "${event}" (${requestId})`));
    }, timeoutMs);

    pendingRequests.set(requestId, {
      resolve: (responseData) => {
        clearTimeout(timeout);
        resolve(responseData as T);
      },
      reject: (err) => {
        clearTimeout(timeout);
        reject(err);
      },
    });

    postToParent({ event, data, requestId });
  });
}

// --- High-level API ---

/** Tell parent we're ready (triggers handshake). */
export function notifyReady(): void {
  postToParent({ event: 'ON_INIT' });
}

/** Tell parent auth is complete. */
export function notifyLoggedIn(): void {
  postToParent({ event: 'AFTER_LOGIN' });
  postToParent({ event: 'callbackLoginSCIO10' });
}

/** Request a Firebase custom token from the SCIO 1.0 parent. */
export function requestCustomToken(): Promise<CustomTokenResponse> {
  // Wire value is camelCase per SCIO 2.0 types.constant.ts
  return sendToParentWithPromise<CustomTokenResponse>('createFirebaseCustomToken');
}

/** Request a fresh Firebase ID token from the parent (fallback for token refresh). */
export function requestFreshToken(forceRefresh = false): Promise<FreshTokenResponse> {
  return sendToParentWithPromise<FreshTokenResponse>('ON_GET_TOKEN_FIREBASE', { forceRefresh });
}

/** Request the active team ID from the parent. */
export function requestTeamId(): Promise<{ teamId: string }> {
  return sendToParentWithPromise<{ teamId: string }>('GET_TEAM');
}

/** Notify parent that we navigated — sends ROUTER_REDIRECT with scio1 state name + query params. */
export function notifyNavigation(path: string): void {
  const [pathname, search] = path.split('?');
  // Strip /app/ prefix to get the route key used in SCIO1_STATE_MAP
  const routeKey = pathname.replace(/^\/app\//, '');
  const stateName = SCIO1_STATE_MAP[routeKey];
  if (!stateName) return;

  const params: Record<string, string> = {};
  if (search) {
    new URLSearchParams(search).forEach((value, key) => {
      params[key] = value;
    });
  }

  postToParent({ event: 'ROUTER_REDIRECT', data: { url: stateName, params } });
}

// --- Parent event listener ---

let listenerCleanup: (() => void) | null = null;

/**
 * Start listening for messages from the SCIO 1.0 parent.
 * Returns a cleanup function. Only one listener active at a time.
 */
export function startParentListener(callbacks: ParentEventCallbacks): () => void {
  // Clean up any previous listener
  listenerCleanup?.();

  const handler = (event: MessageEvent) => {
    // Only handle messages from the parent window
    if (event.source !== window.parent) return;

    let msg: ParentMessage;
    try {
      msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    if (!msg?.event) return;

    const data = msg.data as Record<string, unknown> | undefined;

    switch (msg.event) {
      case 'IS_IFRAME':
        // Not used in Relationship A (scio1 parent → scio3 child).
        // IS_IFRAME is sent by scio3 to scio1 in Relationship B (Scio1Frame.tsx).
        break;

      case 'ROUTER_REDIRECT':
      case 'NAVIGATE_SAME_REMOTE': {
        const url = (data?.url as string) ?? (data?.path as string);
        if (url) {
          callbacks.onRouterRedirect?.(url, data?.params as Record<string, unknown>);
        }
        break;
      }

      case 'CHANGE_TEAM': {
        const teamId = data?.teamId as string;
        if (teamId) {
          callbacks.onChangeTeam?.(teamId);
        }
        break;
      }

      case 'SYNC_LANGUAGE': {
        const language = data?.language as string;
        if (language) {
          callbacks.onSyncLanguage?.(language);
        }
        break;
      }

      case 'LOGOUT':
      case 'FOCUS_LOGOUT':
        callbacks.onLogout?.();
        break;

      case 'reload':
        callbacks.onReload?.();
        break;

      case 'responsePromise': {
        // Resolve pending promise-based requests
        const requestId = msg.requestId;
        if (requestId && pendingRequests.has(requestId)) {
          const pending = pendingRequests.get(requestId)!;
          pendingRequests.delete(requestId);
          pending.resolve(data);
        }
        break;
      }
    }
  };

  window.addEventListener('message', handler);

  listenerCleanup = () => {
    window.removeEventListener('message', handler);
    listenerCleanup = null;
  };

  return listenerCleanup;
}
